import { useMemo, useReducer } from 'react';

export type StagedSelectionId = string | number;

export interface StagedSelectionItem {
  id: StagedSelectionId;
}

interface StagedSelectionState<T extends StagedSelectionItem> {
  itemsById: Map<StagedSelectionId, T>;
  page: number;
  itemsPerPage: number;
}

type StagedSelectionAction<T extends StagedSelectionItem> =
  | { type: 'add'; items: readonly T[] }
  | { type: 'remove'; id: StagedSelectionId }
  | { type: 'clear' }
  | { type: 'setPage'; page: number };

interface StagedSelectionOptions<T extends StagedSelectionItem> {
  initialItems?: readonly T[];
  itemsPerPage?: number;
}

const getPageCount = (itemCount: number, itemsPerPage: number) =>
  Math.max(1, Math.ceil(itemCount / itemsPerPage));

const clampPage = (page: number, itemCount: number, itemsPerPage: number) =>
  Math.min(Math.max(1, page), getPageCount(itemCount, itemsPerPage));

export const createStagedSelectionState = <T extends StagedSelectionItem>({
  initialItems = [],
  itemsPerPage = 10,
}: StagedSelectionOptions<T>): StagedSelectionState<T> => {
  if (itemsPerPage < 1) throw new Error('itemsPerPage must be greater than zero');

  const itemsById = new Map<StagedSelectionId, T>();
  initialItems.forEach((item) => {
    if (!itemsById.has(item.id)) itemsById.set(item.id, item);
  });

  return { itemsById, page: 1, itemsPerPage };
};

export const stagedSelectionReducer = <T extends StagedSelectionItem>(
  state: StagedSelectionState<T>,
  action: StagedSelectionAction<T>,
): StagedSelectionState<T> => {
  switch (action.type) {
    case 'add': {
      const itemsById = new Map(state.itemsById);
      action.items.forEach((item) => {
        if (!itemsById.has(item.id)) itemsById.set(item.id, item);
      });

      return itemsById.size === state.itemsById.size ? state : { ...state, itemsById };
    }
    case 'remove': {
      if (!state.itemsById.has(action.id)) return state;

      const itemsById = new Map(state.itemsById);
      itemsById.delete(action.id);

      return {
        ...state,
        itemsById,
        page: clampPage(state.page, itemsById.size, state.itemsPerPage),
      };
    }
    case 'clear':
      return state.itemsById.size === 0 && state.page === 1
        ? state
        : { ...state, itemsById: new Map(), page: 1 };
    case 'setPage': {
      const page = clampPage(action.page, state.itemsById.size, state.itemsPerPage);
      return page === state.page ? state : { ...state, page };
    }
  }
};

const useStagedSelection = <T extends StagedSelectionItem>(
  options: StagedSelectionOptions<T> = {},
) => {
  const [state, dispatch] = useReducer(
    stagedSelectionReducer<T>,
    options,
    createStagedSelectionState,
  );
  const stagedItems = useMemo(() => Array.from(state.itemsById.values()), [state.itemsById]);
  const pageCount = getPageCount(stagedItems.length, state.itemsPerPage);
  const visibleItems = useMemo(() => {
    const offset = (state.page - 1) * state.itemsPerPage;
    return stagedItems.slice(offset, offset + state.itemsPerPage);
  }, [stagedItems, state.itemsPerPage, state.page]);

  return {
    stagedItems,
    stagedIds: stagedItems.map((item) => item.id),
    visibleItems,
    totalCount: stagedItems.length,
    page: state.page,
    pageCount,
    itemsPerPage: state.itemsPerPage,
    has: (id: StagedSelectionId) => state.itemsById.has(id),
    addItem: (item: T) => dispatch({ type: 'add', items: [item] }),
    addItems: (items: readonly T[]) => dispatch({ type: 'add', items }),
    removeItem: (id: StagedSelectionId) => dispatch({ type: 'remove', id }),
    clear: () => dispatch({ type: 'clear' }),
    setPage: (page: number) => dispatch({ type: 'setPage', page }),
  };
};

export default useStagedSelection;
